/**
 * Orders API service.
 * Mirrors backend service shapes from src/services/orders.ts (API).
 *
 * Backend exposes a few different envelopes:
 *   - GET /orders               -> { orders, total, limit, offset, count }
 *   - GET /orders/:id           -> full order detail object
 *   - GET /customers/me/orders  -> { success, data: { orders, total, ... } }
 *   - GET /sales-reps/me/orders -> { success, data: { orders, total, ... } }
 *   - DELETE /orders/:id        -> { success, data: { ... } }
 * The helpers here normalize all of these.
 */

import { apiClient } from './api';

export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'shipped'
  | 'delivered'
  | 'invoiced'
  | 'cancelled';

export interface OrderItem {
  id?: number;
  sku_id: number;
  sku_code?: string;
  product_name?: string;
  quantity: number;
  unit_price: number;
  line_total?: number;
  tax_amount?: number;
}

export interface Order {
  id: number;
  customer_id: number;
  customer_name?: string;
  order_date: string;
  status: OrderStatus;
  total_amount: number;
  tax_total?: number;
  items?: OrderItem[];
  status_history?: OrderStatusHistoryEntry[];
  invoice?: { id: number; invoice_number: string; status: string } | null;
  shipping_address_id?: number | null;
  created_by_user_id?: number | null;
  created_at: string;
  updated_at: string;
  confirmed_at?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
  cancelled_at?: string | null;
}

export interface OrderStatusHistoryEntry {
  from_status: string | null;
  to_status: string;
  changed_at: string;
  changed_by_user_id: number | null;
  reason: string | null;
}

export interface OrderSummary {
  total: number;
  pending: number;
  confirmed: number;
  shipped: number;
  delivered: number;
  invoiced: number;
  cancelled: number;
  total_revenue: number;
  weekly_revenue: number;
}

export interface ListOrdersFilters {
  status?: OrderStatus;
  customer_id?: number;
  from_date?: string;
  to_date?: string;
  sort_by?: 'created_at' | 'status' | 'total_amount';
  limit?: number;
  offset?: number;
}

export interface CreateOrderItemInput {
  sku_id: number;
  quantity: number;
  unit_price: number;
}

export interface CreateOrderInput {
  customer_id?: number; // required for Sales Rep / Call Center / Admin
  shipping_address_id?: number;
  items: CreateOrderItemInput[];
}

/**
 * The three list endpoints return three slightly different row shapes:
 *
 *   GET /orders                 -> { id,        ...,                  created_at }
 *   GET /customers/me/orders    -> { order_id,  ..., item_count,      created_at }
 *   GET /sales-reps/me/orders   -> { order_id,  ..., item_count,      order_date, created_at }
 *
 * To keep the frontend `Order` contract stable across all three pages
 * (AdminOrders / CustomerOrders / SalesRepOrders), we normalize each row
 * into the canonical shape here:
 *   - `id`         comes from `id` or falls back to `order_id`
 *   - `order_date` comes from `order_date` or falls back to `created_at`
 *   - `items`      is materialized from `line_items`/`items` when present,
 *                  otherwise we synthesize a length-only array using
 *                  `item_count` so callers that only need `items.length`
 *                  keep working.
 */
function normalizeOrderRow(row: any): Order {
  if (!row || typeof row !== 'object') return row;
  const id = row.id ?? row.order_id;
  const orderDate = row.order_date ?? row.created_at;

  let items = row.items ?? row.line_items;
  if (!Array.isArray(items) && typeof row.item_count === 'number') {
    // Synthesize so `items?.length` works without forcing every page to
    // know about the alternate `item_count` field.
    items = Array.from({ length: row.item_count }, () => ({} as OrderItem));
  }

  return {
    ...row,
    id,
    order_date: orderDate,
    items,
  } as Order;
}

function normalizeListResponse(raw: any): { data: Order[]; total: number; limit: number; offset: number } {
  // Some endpoints wrap in { success, data: { orders, total, ... } }
  const inner = raw?.data?.orders !== undefined ? raw.data : raw;
  const rawOrders: any[] = inner?.orders ?? [];
  return {
    data: rawOrders.map(normalizeOrderRow),
    total: inner?.total ?? 0,
    limit: inner?.limit ?? 20,
    offset: inner?.offset ?? 0,
  };
}

export async function listOrders(filters: ListOrdersFilters = {}): Promise<{
  data: Order[];
  total: number;
  limit: number;
  offset: number;
}> {
  const raw = await apiClient.get<any>('/orders', filters as Record<string, any>);
  return normalizeListResponse(raw);
}

export async function getOrder(id: number | string): Promise<Order> {
  const raw = await apiClient.get<any>(`/orders/${id}`);
  return normalizeOrderRow(raw);
}

export async function getOrderStatusHistory(id: number | string): Promise<{
  order_id: number;
  status_history: OrderStatusHistoryEntry[];
  count: number;
}> {
  return apiClient.get(`/orders/${id}/status-history`);
}

export async function getOrdersSummary(): Promise<OrderSummary> {
  return apiClient.get<OrderSummary>('/orders/summary');
}

export async function createOrder(input: CreateOrderInput): Promise<Order> {
  return apiClient.post<Order>('/orders', input);
}

export async function cancelOrder(id: number | string): Promise<{
  order_id: number;
  previous_status: OrderStatus;
  new_status: 'cancelled';
  cancelled_at: string;
  inventory_released_count: number;
}> {
  // Backend wraps response as { success, data: {...} } — apiClient.delete unwraps `data`
  return apiClient.delete(`/orders/${id}`);
}

/**
 * Creates a new pending order from an existing one's items + customer.
 * Backend resolves shipping address from the body or the customer's default.
 */
export async function duplicateOrder(
  id: number | string,
  shippingAddressId?: number
): Promise<Order> {
  return apiClient.post<Order>(
    `/orders/${id}/duplicate`,
    shippingAddressId ? { shipping_address_id: shippingAddressId } : {}
  );
}

export async function listMyCustomerOrders(filters: ListOrdersFilters = {}): Promise<{
  data: Order[];
  total: number;
  limit: number;
  offset: number;
}> {
  const raw = await apiClient.get<any>('/customers/me/orders', filters as Record<string, any>);
  return normalizeListResponse(raw);
}

/**
 * Warehouse confirms an order. Moves order pending → confirmed (UI "Processing").
 * Required gate before pick-pack. Writes an order_status_history row.
 */
export async function confirmOrder(
  orderId: number | string,
  notes?: string
): Promise<{ ok: boolean; orderId: number; status: 'confirmed' }> {
  return apiClient.post(`/orders/${orderId}/confirm`, notes ? { notes } : {});
}

/**
 * Warehouse pick-pack confirm. Moves order confirmed → shipped on the backend
 * and writes an order_status_history row.
 * Backend rejects with 409 ORDER_NOT_CONFIRMED if the order isn't confirmed yet.
 */
export async function completePicking(
  orderId: number | string,
  notes?: string
): Promise<{ ok: boolean; orderId: number; status: 'shipped' }> {
  return apiClient.post(`/orders/${orderId}/complete-picking`, notes ? { notes } : {});
}

export async function listMySalesRepOrders(filters: ListOrdersFilters = {}): Promise<{
  data: Order[];
  total: number;
  limit: number;
  offset: number;
}> {
  const raw = await apiClient.get<any>('/sales-reps/me/orders', filters as Record<string, any>);
  return normalizeListResponse(raw);
}

export interface SuggestedReorderLine {
  sku_id: number;
  sku_code: string;
  product_name: string;
  suggested_quantity: number;
  based_on_orders: number;
  last_ordered_at: string;
}

/**
 * Basic suggested-reorder quantities (VP-SALES-004, Phase 1 scope) — average
 * quantity per SKU from the customer's recent order history. Backend:
 * GET /orders/customers/:customerId/suggested-reorder.
 */
export async function getSuggestedReorder(
  customerId: number | string
): Promise<SuggestedReorderLine[]> {
  const raw = await apiClient.get<any>(`/orders/customers/${customerId}/suggested-reorder`);
  return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
}

export interface PickDetailLine {
  sku_id: number;
  sku_code: string;
  sku_name: string;
  lot_id: number;
  lot_number: string;
  location_code: string | null;
  quantity: number;
}

/**
 * FEFO pick-detail guidance (VP-WMS-007): the real lot + warehouse location
 * already allocated for this order at creation time, per reserved line.
 * Backend: GET /orders/:orderId/pick-detail.
 */
export async function getPickDetail(orderId: number | string): Promise<PickDetailLine[]> {
  const raw = await apiClient.get<any>(`/orders/${orderId}/pick-detail`);
  return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
}
